import React, { useCallback, useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Dialog, IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import api from "../../services/api";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import { generateRouteDispatchClientId } from "../../utils/routeDispatchClientId";
import { extractConnectionOptions } from "../../utils/routeConnectionOptions";
import { useRouteFlow, flowNow, livePointListeners } from "./routeFlowStore";

// LOCALIZAÇÃO AO VIVO DO MOTOBOY — CRM (20/10/2026, prévia aprovada
// https://claude.ai/artifact/UJphY1DS6cCMQuvPdN9rcy). O motoboy liga no app
// Visita; aqui o grupo da rota ganha o 📍 animado no fim do título, a faixa
// fixada "Localização em tempo real" e a janela com o mapa (caminho do dia,
// ponto piscando onde ele está) e os km de cliente em cliente.

const FRESH_MS = 5 * 60 * 1000;
const PURPLE = "#8b5cf6";

const css = `
@keyframes llBob{50%{transform:translateY(-4px)}}
@keyframes llShadow{50%{transform:scale(.6);opacity:.4}}
@keyframes llPing{70%{box-shadow:0 0 0 8px rgba(139,92,246,0)}100%{box-shadow:0 0 0 0 rgba(139,92,246,0)}}
@keyframes llPing2{70%{box-shadow:0 0 0 16px rgba(139,92,246,0)}100%{box-shadow:0 0 0 0 rgba(139,92,246,0)}}
.ll-pin{border:none;background:transparent;cursor:pointer;font-size:19px;line-height:1;padding:2px 4px;position:relative;animation:llBob 1.6s ease-in-out infinite}
.ll-pin::after{content:"";position:absolute;left:50%;bottom:-3px;width:12px;height:4px;margin-left:-6px;border-radius:50%;background:rgba(139,92,246,.65);animation:llShadow 1.6s ease-in-out infinite}
.ll-pin.stale{animation:none;filter:grayscale(1);opacity:.6}.ll-pin.stale::after{display:none}
.ll-dot{width:9px;height:9px;border-radius:50%;background:#8b5cf6;flex-shrink:0;animation:llPing 1.4s infinite}
.ll-dot.stale{background:#8696a0;animation:none}
.ll-me{width:34px;height:34px;border-radius:50%;background:#fff;display:grid;place-items:center;font-size:17px;animation:llPing2 1.3s infinite}
.ll-me.off{animation:none;filter:grayscale(1)}
.ll-cpin{width:26px;height:26px;border-radius:50%;background:#25d366;color:#06210f;font-weight:800;font-size:13px;display:grid;place-items:center;border:3px solid #0b141a}
.ll-cpin.cur{background:#e5484d;color:#fff}
.ll-popwrap .leaflet-popup-content-wrapper{background:#1f2c34;color:#e9edef;border-radius:14px;box-shadow:0 8px 24px rgba(0,0,0,.5)}
.ll-popwrap .leaflet-popup-tip{background:#1f2c34}
.ll-popwrap .leaflet-popup-content{margin:12px 14px}
.ll-pop{min-width:180px;font-family:inherit}
.ll-pop-t{font-weight:800;font-size:15px}
.ll-pop-s{font-size:12px;color:#8696a0;margin-top:2px}
.ll-pop-b{display:flex;gap:8px;margin-top:10px}
.ll-pop-b button{flex:1;border:none;border-radius:999px;padding:8px 10px;font-weight:700;font-size:13.5px;cursor:pointer;font-family:inherit}
.ll-pop-no{background:#2a3942;color:#e9edef}
.ll-pop-ok{background:#8b5cf6;color:#fff}
.ll-cpin.dev{background:#e5484d;color:#fff}
.ll-dot-dev{width:14px;height:14px;border-radius:50%;background:#e5484d;border:2px solid #fff;box-shadow:0 0 0 2px rgba(229,72,77,.35)}
.ll-dot-dev.falta{background:#f7c948}
.ll-cli{min-width:220px;font-family:inherit}
.ll-cli-n{font-weight:800;font-size:14.5px}
.ll-cli-e{font-size:11.5px;color:#8696a0;margin-top:2px}
.ll-cli-st{display:inline-block;margin-top:6px;font-size:11px;font-weight:800;border-radius:999px;padding:2px 8px;background:rgba(229,72,77,.18);color:#ff9a9d}
.ll-cli-st.falta{background:rgba(247,201,72,.18);color:#f7c948}
.ll-cli-g{display:grid;grid-template-columns:auto 1fr;gap:3px 10px;margin-top:8px;font-size:12.5px}
.ll-cli-g span{color:#8696a0}
.ll-cli-g b{text-align:right;font-variant-numeric:tabular-nums}
.ll-cli-go{width:100%;margin-top:10px;border:none;border-radius:999px;padding:9px 10px;background:#8b5cf6;color:#fff;font-weight:800;font-size:13.5px;cursor:pointer;font-family:inherit}
.ll-cli-go:disabled{opacity:.6}
@media (prefers-reduced-motion:reduce){.ll-pin,.ll-pin::after,.ll-dot,.ll-me{animation:none}}
`;
let cssDone = false;
const ensureCss = () => {
  if (cssDone || typeof document === "undefined") return;
  const el = document.createElement("style");
  el.textContent = css;
  document.head.appendChild(el);
  cssDone = true;
};

const liveOf = (st) => (st && st.live && st.live.sharing ? st.live : null);
const isFresh = (live) => Boolean(live && live.at && flowNow() - new Date(live.at).getTime() < FRESH_MS);
const ago = (at) => {
  if (!at) return "aguardando o 1º ponto";
  const s = Math.max(0, Math.round((flowNow() - new Date(at).getTime()) / 1000));
  if (s < 60) return `há ${s}s`;
  if (s < 3600) return `há ${Math.round(s / 60)} min`;
  return `há ${Math.floor(s / 3600)}h`;
};
const nomeCurto = (st) => String((st && st.courierName) || "motoboy").replace(/^\s*rota\s*-\s*/i, "").trim() || "motoboy";
const fmtKm = (v) => `${(Number(v) || 0).toFixed(1).replace(".", ",")} km`;
const hhmm = (t) => (t ? new Date(t).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "");

// 29/10/2026: desliza a moto até a posição nova em vez de "teleportar" --
// tanto o ponto que chega pelo socket quanto o redesenho de 60s passam por
// aqui, sempre no MESMO marker (setLatLng muda o marcador existente; nunca
// remove+recria), senão não tem como o movimento ficar contínuo.
const animateMarkerTo = (marker, to, duration = 1800) => {
  if (!marker) return;
  if (marker._llAnimFrame) cancelAnimationFrame(marker._llAnimFrame);
  const from = marker.getLatLng();
  const toLatLng = L.latLng(to[0], to[1]);
  if (from.equals(toLatLng)) return;
  const start = performance.now();
  const step = (now) => {
    const t = Math.min(1, (now - start) / duration);
    marker.setLatLng([
      from.lat + (toLatLng.lat - from.lat) * t,
      from.lng + (toLatLng.lng - from.lng) * t
    ]);
    marker._llAnimFrame = t < 1 ? requestAnimationFrame(step) : null;
  };
  marker._llAnimFrame = requestAnimationFrame(step);
};

// 24/10/2026: clicar na moto -> balão "Abrir no mapa?" -> Google Maps na posição atual dele.
const bindMePopup = (mk) => {
  if (!mk) return;
  const box = document.createElement("div");
  box.className = "ll-pop";
  box.innerHTML =
    '<div class="ll-pop-t">Abrir no mapa?</div><div class="ll-pop-s">Localização atual do motoboy</div>' +
    '<div class="ll-pop-b"><button type="button" class="ll-pop-no">Cancelar</button><button type="button" class="ll-pop-ok">Abrir</button></div>';
  box.querySelector(".ll-pop-no").onclick = () => mk.closePopup();
  box.querySelector(".ll-pop-ok").onclick = () => {
    const p = mk.getLatLng();
    mk.closePopup();
    window.open(`https://www.google.com/maps/search/?api=1&query=${p.lat.toFixed(6)},${p.lng.toFixed(6)}`, "_blank", "noopener");
  };
  mk.bindPopup(box, { closeButton: false, offset: [0, -14], className: "ll-popwrap" });
};

// 24/10/2026: pop-up do cliente (devendo/falta) com botão de mandar rota pro motoboy do mapa.
const esc = (v) => String(v ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
const mandarRota = async (courierId, info, btn) => {
  btn.disabled = true;
  btn.textContent = "Mandando…";
  const body = {
    courierIds: [Number(courierId)],
    contactId: info.contactId,
    registrationIds: info.registrationIds,
    recheckPaymentBeforeDispatch: true,
    clientId: generateRouteDispatchClientId()
  };
  try {
    try {
      await api.post("/couriers/send-route", body);
    } catch (err) {
      // Cliente com conversa em mais de uma conexão: manda pelo Escritório.
      const opts = extractConnectionOptions(err);
      if (!opts) throw err;
      const esc = opts.find((o) => /escrit/i.test(String(o.connLabel || ""))) || opts[0];
      await api.post("/couriers/send-route", { ...body, connectionId: esc.whatsappId, clientId: generateRouteDispatchClientId() });
    }
    toast.success(`Rota de ${info.nome} enviada`);
    btn.textContent = "✓ Rota enviada";
  } catch (err) {
    toastError(err);
    btn.disabled = false;
    btn.textContent = "🛵 Mandar rota";
  }
};
const clientePopup = (courierId, courierNome, info, extra) => {
  const box = document.createElement("div");
  box.className = "ll-cli";
  const falta = /falta/i.test(info.status || "");
  box.innerHTML =
    `<div class="ll-cli-n">${extra ? esc(extra) + " · " : ""}${esc(info.nome)}</div>` +
    `<div class="ll-cli-e">${esc(info.endereco || "")}${info.distanciaKm != null ? " · " + String(info.distanciaKm).replace(".", ",") + " km do motoboy" : ""}</div>` +
    `<span class="ll-cli-st${falta ? " falta" : ""}">${esc(info.status)}</span>` +
    `<div class="ll-cli-g"><span>Atraso</span><b>${esc(info.atraso || "—")}</b><span>Valor</span><b>${esc(info.valor)}</b><span>Juros</span><b>${esc(info.juros)}</b><span>Falta</span><b>${esc(info.falta)}</b></div>` +
    (info.rotaProibida ? '<div class="ll-cli-e" style="color:#ff9a9d;margin-top:8px">🚫 Rota proibida pra esse cliente</div>' : `<button type="button" class="ll-cli-go">🛵 Mandar rota pro ${esc(courierNome)}</button>`);
  const b = box.querySelector(".ll-cli-go");
  if (b) b.onclick = () => mandarRota(courierId, info, b);
  return box;
};

// Janela do mapa (um por vez na tela).
const openers = new Set();
export const openLiveMap = (st) => openers.forEach((fn) => fn(st));

export const LiveLocationTitlePin = ({ ticket }) => {
  ensureCss();
  const st = useRouteFlow(ticket);
  const live = liveOf(st);
  if (!live) return null;
  return (
    <button
      type="button"
      className={`ll-pin${isFresh(live) ? "" : " stale"}`}
      title={`Ver onde o ${nomeCurto(st)} está`}
      aria-label={`Ver onde o ${nomeCurto(st)} está`}
      onClick={(e) => { e.stopPropagation(); openLiveMap(st); }}
    >
      📍
    </button>
  );
};

export const LiveLocationListChip = ({ ticket }) => {
  ensureCss();
  const st = useRouteFlow(ticket);
  const live = liveOf(st);
  if (!live) return null;
  const fresh = isFresh(live);
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); openLiveMap(st); }}
      style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: "0.7rem", fontWeight: 700, color: fresh ? "#c4b0ff" : "#8696a0", margin: "2px 0", cursor: "pointer" }}
    >
      <span className={`ll-pin${fresh ? "" : " stale"}`} style={{ fontSize: 13, padding: 0 }}>📍</span>
      {fresh ? "ao vivo" : `parou ${ago(live.at)}`}
    </span>
  );
};

export const LiveLocationBar = ({ ticket }) => {
  ensureCss();
  const st = useRouteFlow(ticket);
  const [, setTick] = useState(0);
  const live = liveOf(st);
  useEffect(() => {
    if (!live) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 2000);
    return () => clearInterval(t);
  }, [live]);
  if (!live) return null;
  const fresh = isFresh(live);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => openLiveMap(st)}
      onKeyDown={(e) => { if (e.key === "Enter") openLiveMap(st); }}
      style={{
        display: "flex", alignItems: "center", gap: 8, padding: "7px 14px", cursor: "pointer",
        background: fresh ? "#14232b" : "#2a1a14", borderBottom: "1px solid rgba(255,255,255,.08)", color: "#e9edef", fontSize: 13
      }}
    >
      <span className={`ll-dot${fresh ? "" : " stale"}`} />
      <span style={{ flex: 1, minWidth: 0 }}>
        📌 <b>Localização em tempo real</b> · {nomeCurto(st)} · {fresh ? ago(live.at) : `parou ${ago(live.at)}`}
      </span>
      <span style={{ color: "#a78bfa", fontWeight: 800 }}>Ver no mapa</span>
    </div>
  );
};

export const LiveLocationDialogHost = () => {
  ensureCss();
  const [st, setSt] = useState(null);
  const [track, setTrack] = useState(null);
  const [, setTick] = useState(0);
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const lineRef = useRef(null);
  const meRef = useRef(null);
  const clientsRef = useRef(null);
  const trackRef = useRef(null);
  const nearbyRef = useRef(null);
  const nearbyLayerRef = useRef(null);

  useEffect(() => {
    const fn = (s) => setSt(s);
    openers.add(fn);
    return () => openers.delete(fn);
  }, []);

  const draw = useCallback((d, fit) => {
    const map = mapRef.current;
    if (!map || !d) return;
    const ll = (d.path || []).map((p) => [p[0], p[1]]);
    if (d.live && d.live.sharing && Number.isFinite(Number(d.live.lat))) ll.push([Number(d.live.lat), Number(d.live.lng)]);
    if (lineRef.current) lineRef.current.remove();
    lineRef.current = L.polyline(ll, { color: PURPLE, weight: 6, opacity: 0.9 }).addTo(map);
    if (clientsRef.current) clientsRef.current.remove();
    clientsRef.current = L.layerGroup().addTo(map);
    const cl = d.clients || [];
    const por = (nearbyRef.current && nearbyRef.current.porContato) || {};
    const naRota = new Set(cl.map((c) => Number(c.contactId)).filter(Boolean));
    cl.forEach((c, i) => {
      if (c.lat === null || !Number.isFinite(Number(c.lat))) return;
      const info = c.contactId ? por[c.contactId] : null;
      const mk = L.marker([c.lat, c.lng], {
        icon: L.divIcon({ className: "", html: `<div class="ll-cpin${info ? " dev" : ""}">${i + 1}</div>`, iconSize: [26, 26], iconAnchor: [13, 13] })
      }).addTo(clientsRef.current);
      if (info) mk.bindPopup(clientePopup(d.courierId, d.courierName, info, `${i + 1}`), { closeButton: false, offset: [0, -10], className: "ll-popwrap" });
      else mk.bindTooltip(`${i + 1} · ${String(c.name || "")} · em dia`);
    });
    // Bolinhas vermelhas (devendo) / amarelas (falta) em volta do motoboy.
    if (nearbyLayerRef.current) nearbyLayerRef.current.remove();
    nearbyLayerRef.current = L.layerGroup().addTo(map);
    ((nearbyRef.current && nearbyRef.current.clientes) || []).forEach((info) => {
      if (naRota.has(Number(info.contactId)) || !Number.isFinite(Number(info.lat))) return;
      L.marker([info.lat, info.lng], {
        icon: L.divIcon({ className: "", html: `<div class="ll-dot-dev${/falta/i.test(info.status) ? " falta" : ""}"></div>`, iconSize: [14, 14], iconAnchor: [7, 7] })
      })
        .bindPopup(clientePopup(d.courierId, d.courierName, info), { closeButton: false, offset: [0, -6], className: "ll-popwrap" })
        .addTo(nearbyLayerRef.current);
    });
    const cur = ll[ll.length - 1];
    if (cur) {
      const icon = L.divIcon({ className: "", html: `<div class="ll-me${d.live && d.live.sharing ? "" : " off"}"><span>🛵</span></div>`, iconSize: [34, 34], iconAnchor: [17, 17] });
      if (meRef.current) {
        // já existe -- só atualiza ícone (liga/desliga) e desliza pra posição
        // nova; recriar aqui era o que fazia a moto "piscar" a cada 60s.
        meRef.current.setIcon(icon);
        animateMarkerTo(meRef.current, cur);
      } else {
        meRef.current = L.marker(cur, { icon, zIndexOffset: 1000 }).addTo(map);
        bindMePopup(meRef.current);
      }
    } else if (meRef.current) {
      meRef.current.remove();
      meRef.current = null;
    }
    if (fit) {
      const pts = ll.concat(cl.filter((c) => c.lat !== null && Number.isFinite(Number(c.lat))).map((c) => [c.lat, c.lng]));
      if (pts.length > 1) map.fitBounds(pts, { padding: [34, 34] });
      else if (pts.length) map.setView(pts[0], 15);
    }
  }, []);

  const load = useCallback(async (fit) => {
    if (!st) return;
    const m = mapRef.current;
    if (!fit && m && m._popup && m.hasLayer(m._popup)) return; // pop-up aberto: não redesenha agora
    try {
      const [{ data }, near] = await Promise.all([
        api.get(`/route-flow/${st.courierId}/live-track`),
        api.get(`/route-flow/${st.courierId}/live-nearby`, { params: { raio: 3 } }).catch(() => ({ data: null }))
      ]);
      nearbyRef.current = near && near.data;
      trackRef.current = data;
      setTrack(data);
      draw(data, fit);
    } catch (err) {
      setTrack({ error: true });
    }
  }, [st, draw]);

  const onEntered = () => {
    if (!mapEl.current) return;
    if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    mapRef.current = L.map(mapEl.current, { zoomControl: true, attributionControl: false }).setView([-23.5, -46.4], 12);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19, subdomains: "abc" }).addTo(mapRef.current);
    load(true);
  };

  // Ponto novo: estica a linha e move a moto na hora; km recalcula a cada 60s.
  useEffect(() => {
    if (!st) return undefined;
    const onPoint = (p) => {
      if (String(p.courierId) !== String(st.courierId)) return;
      const d = trackRef.current;
      if (d) d.live = { ...(d.live || {}), sharing: true, lat: p.lat, lng: p.lng, at: p.at, speed: p.speed };
      if (lineRef.current) lineRef.current.addLatLng([p.lat, p.lng]);
      if (meRef.current) animateMarkerTo(meRef.current, [p.lat, p.lng]);
      setTick((n) => n + 1);
    };
    livePointListeners.add(onPoint);
    const t1 = setInterval(() => setTick((n) => n + 1), 1000);
    const t2 = setInterval(() => load(false), 60000);
    return () => { livePointListeners.delete(onPoint); clearInterval(t1); clearInterval(t2); };
  }, [st, load]);

  const close = () => {
    if (meRef.current && meRef.current._llAnimFrame) cancelAnimationFrame(meRef.current._llAnimFrame);
    if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    lineRef.current = null; meRef.current = null; clientsRef.current = null; trackRef.current = null;
    nearbyRef.current = null; nearbyLayerRef.current = null;
    setTrack(null);
    setSt(null);
  };

  const live = track && track.live;
  const speed = live && live.speed !== null && live.speed !== undefined && Number.isFinite(Number(live.speed)) ? ` · ${Math.round(Number(live.speed) * 3.6)} km/h` : "";
  const sub = live && live.sharing && live.at ? `atualizado ${ago(live.at)}${speed}` : live && live.at ? `localização desligada · última ${ago(live.at)}` : "sem localização ainda";
  const legs = (track && track.legs) || [];

  return (
    <Dialog open={Boolean(st)} onClose={close} maxWidth="md" fullWidth TransitionProps={{ onEntered }}
      PaperProps={{ style: { background: "#111b21", color: "#e9edef", overflow: "hidden" } }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", background: "#1f2c34" }}>
        <div style={{ width: 38, height: 38, borderRadius: "50%", background: "#2f4f3f", display: "grid", placeItems: "center", fontWeight: 700 }}>
          {nomeCurto(st).slice(0, 1).toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{nomeCurto(st)} · ao vivo</div>
          <div style={{ color: "#8696a0", fontSize: 12.5 }}>{track ? sub : "carregando…"}</div>
        </div>
        <IconButton onClick={close} style={{ color: "#e9edef" }} aria-label="Fechar mapa"><CloseIcon /></IconButton>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 320px", minHeight: 480 }}>
        <div ref={mapEl} style={{ minHeight: 480, background: "#0e1a20" }} />
        <div style={{ borderLeft: "1px solid #2a3942", overflow: "auto", maxHeight: 520 }}>
          <div style={{ padding: "12px 14px 6px", fontSize: 12.5, letterSpacing: ".05em", textTransform: "uppercase", color: "#8696a0", fontWeight: 700 }}>
            Km de hoje · pelo caminho real
          </div>
          {!track && <div style={{ padding: 14, color: "#8696a0", fontSize: 13.5 }}>Carregando o caminho…</div>}
          {track && track.error && <div style={{ padding: 14, color: "#8696a0", fontSize: 13.5 }}>Não consegui carregar o caminho.</div>}
          {track && !track.error && !legs.length && (
            <div style={{ padding: 14, color: "#8696a0", fontSize: 13.5 }}>
              {(track.path || []).length ? "Nenhuma rota despachada ainda hoje." : "Ainda sem caminho hoje."}
            </div>
          )}
          {legs.map((l, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "auto minmax(0,1fr) auto", gap: 10, alignItems: "center", padding: "10px 14px", borderTop: "1px solid #2a3942" }}>
              <span style={{ width: 24, height: 24, borderRadius: "50%", display: "grid", placeItems: "center", fontSize: 12, fontWeight: 800, background: l.ongoing ? PURPLE : "#2a3942", color: l.ongoing ? "#fff" : "#e9edef" }}>{i + 1}</span>
              <div style={{ fontSize: 13.5, lineHeight: 1.35, minWidth: 0 }}>
                {l.from} → {l.to}
                {l.ongoing && <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 6, padding: "1px 6px", marginLeft: 6, background: "rgba(139,92,246,.2)", color: "#c4b0ff" }}>agora</span>}
                <span style={{ display: "block", color: "#8696a0", fontSize: 12 }}>desde {hhmm(l.startAt)}</span>
              </div>
              <div style={{ fontWeight: 800, fontSize: 15, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{fmtKm(l.km)}</div>
            </div>
          ))}
          {track && !track.error && (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", borderTop: "1px solid #2a3942", background: "#101c22" }}>
              <span style={{ color: "#8696a0", fontSize: 12.5 }}>Total andado hoje</span>
              <b style={{ fontSize: 18, fontVariantNumeric: "tabular-nums" }}>{fmtKm(track.totalKm)}</b>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};
